<template>
  <nuxt-link
    ref="card"
    :to="slide.link"
    class="article-card"
    @click="sendCriteoBeacon(slide.beacons?.clickBeacon)"
  >
    <div class="card-top">
      <BasicPillBox :pills="pills" />
      <div class="flex flex-col">
        <ArticleExpMedium
          class="self-center"
          :medium-id="slide.expArticle.primaryImage"
          preset="ppmArticleImage"
        />
        <ArticleEnergyEfficiency
          :article="slide.expArticle"
          horizontal-layout
        />
      </div>
      <div class="w-full">
        <ClientOnly
          fallback-tag="span"
          fallback="Wird geladen..."
        >
          <LazyArticleRatingStars :article-id="slide.expArticle.articleId" />
        </ClientOnly>
      </div>
      <div class="w-full line-clamp-3 card-title">
        <span class="manufacturer font-bold">{{ brandName }}</span> {{ articleName }}
      </div>
    </div>
    <div class="cart-bottom articleStatus">
      <div
        v-if="isCrawler == false"
        class="card-price"
      >
        <ArticlePriceCompact
          class="compact-price w-full"
          :article="slide.expArticle"
          with-promotions
          :full-price="slide.expPrice"
        />
        <ArticleDeliveryInfo
          :localprice="slide.expPrice"
          :pprice="slide.expPrice"
          :shipment-brutto-price="shippingCost"
          hide-shipment-price
          :several-shipment-options="hasSeveralShipmentOptions"
        />
      </div>
    </div>
  </nuxt-link>
</template>

<script setup lang="ts">
import type { NeoService } from '@expert/types';
import type { TemplateRef } from 'vue';
import { getExpManufacturerByBrandId } from '~/composables/ExpApi/expCache';
import { sendCriteoBeacon, useCriteoBeacons, usePlacementBeacon } from '~/composables/sponsoring';

interface Props {
  slide: ArticleSlide;
  isSponsored?: boolean;
}

const props = defineProps<Props>();
const isCrawler: Ref<boolean> = useIsCrawlerState();
const card: TemplateRef<HTMLElement> = useTemplateRef<HTMLElement>('card');
const { placementBeaconString } = usePlacementBeacon();

const brandName: Ref<string | undefined> = ref();
if (props.slide.brand) brandName.value = props.slide.brand;
else if (props.slide.expArticle?.brandId) {
  const response = await getExpManufacturerByBrandId(props.slide.expArticle.brandId);

  if (response?.data) {
    brandName.value = response?.data.value?.name;
  }
}

const articleName: ComputedRef<string> = computed(() => {
  const articleHeadline: string | undefined = props.slide?.expArticle?.headline;

  if (articleHeadline && brandName.value && articleHeadline.startsWith(brandName.value)) {
    return articleHeadline.replace(brandName.value, '').trim();
  } else {
    return articleHeadline ?? '';
  }
});

const promotions: ComputedRef<Array<ArticlePromotion>> = computed(() => {
  return getPromotionsForArticle(props.slide.expArticle.articleId ?? 'ANY', props.slide.expPrice);
});

const pills: ComputedRef<Array<Pill>> = computed(() => {
  const pillList: Array<Pill> = [];
  if (props.isSponsored) {
    pillList.push({
      text: 'Gesponsert',
    });
  } else if (props.slide?.expPrice?.itemOnDisplay?.onDisplay) {
    return [mapPillFromExhibition(props.slide?.expPrice.itemOnDisplay)];
  }

  if (promotions.value && promotions.value.length > 0) {
    pillList.push(...promotions.value.map(mapPillFromPromotion));
  }

  return pillList;
});

const hasSeveralShipmentOptions = computed(() => {
  const shipments = props.slide?.expPrice?.shipmentArray;
  const deliveryInfos = props.slide?.expPrice?.availableServices?.find((service: NeoService) => service.label === 'Lieferung');
  return (shipments?.length || 0) + (deliveryInfos?.price?.gross ? 1:0) > 1
})

const shippingCost = computed(() => {
  const shipments = props.slide?.expPrice?.shipmentArray;
  const deliveryInfos = props.slide?.expPrice?.availableServices?.find((service: NeoService) => service.label === 'Lieferung');

  if (!shipments?.length) return null;

  const prices = shipments
    .map(shipment => shipment.shipmentBruttoPrice)
    .filter(price => price != null);

  const minPrice = prices.length ? Math.min(...prices) : null;
  const reservationPrice = deliveryInfos?.price?.gross ? deliveryInfos.price.gross.toFixed(2) : null
  return (minPrice && reservationPrice ? Math.min(minPrice, reservationPrice) : minPrice || reservationPrice);
});

if (props.isSponsored && props.slide?.beacons) {
  props.slide.beacons.loadBeacon += placementBeaconString.value;
  useCriteoBeacons(card, props.slide.beacons);
}
</script>

<style scoped lang="postcss">
.article-card {
  @apply p-4 flex flex-col justify-between bg-white border rounded-3xl;
}

.card-price {
  @apply text-xs;
}

.card-top {
  @apply flex flex-col w-full;
}

.card-title {
  color: rgb(var(--paragraph-light));

  .manufacturer {
    color: rgb(var(--paragraph));
  }
}

.cart-bottom {
  @apply flex flex-col w-full;

  & :deep(.error-box),
  & :deep(.error-box .container),
  & :deep(.error-box .info) {
    padding: 0;
  }

  & :deep(.error-box button > div) {
    padding: 1rem;
    font-size: 13px;
  }
}
</style>
